import { useState } from "react";
import { C } from "../utils/tokens";

export const Orb = ({ x, y, s, c, b = 120 }) => (
  <div
    style={{
      position: "fixed",
      left: `${x}%`,
      top: `${y}%`,
      width: s,
      height: s,
      borderRadius: "50%",
      background: c,
      filter: `blur(${b}px)`,
      opacity: 0.11,
      pointerEvents: "none",
      zIndex: 0,
      transform: "translate(-50%,-50%)",
    }}
  />
);

export const Grid = () => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      pointerEvents: "none",
      zIndex: 0,
      backgroundImage: `linear-gradient(rgba(245,197,24,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(245,197,24,0.025) 1px,transparent 1px)`,
      backgroundSize: "70px 70px",
    }}
  />
);

export const Tag = ({ children, color = C.gold }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      background: `${color}18`,
      border: `1px solid ${color}2f`,
      color,
      borderRadius: 4,
      padding: "3px 10px",
      fontSize: 10.5,
      fontFamily: "'DM Mono',monospace",
      letterSpacing: "0.07em",
      textTransform: "uppercase",
    }}
  >
    {children}
  </span>
);

export const Spinner = ({ size = 16 }) => (
  <div
    style={{
      width: size,
      height: size,
      border: `2px solid rgba(245,197,24,0.3)`,
      borderTopColor: C.gold,
      borderRadius: "50%",
      animation: "spin 0.7s linear infinite",
      flexShrink: 0,
    }}
  />
);

export const Btn = ({
  children,
  onClick,
  variant = "primary",
  size = "md",
  style,
  disabled,
  loading,
}) => {
  const p = { sm: "7px 16px", md: "10px 24px", lg: "13px 34px" }[size];
  const fs = { sm: 11.5, md: 13, lg: 15 }[size];
  const v = {
    primary: {
      background: `linear-gradient(135deg,${C.gold},#D4940A)`,
      color: "#080909",
      boxShadow: `0 4px 20px rgba(245,197,24,0.22)`,
    },
    secondary: {
      background: C.surface,
      border: `1px solid ${C.border}`,
      color: C.muted,
    },
    ghost: {
      background: "transparent",
      border: `1px solid ${C.goldBorder}`,
      color: C.gold,
    },
    danger: {
      background: "rgba(255,82,82,0.1)",
      border: `1px solid rgba(255,82,82,0.25)`,
      color: C.danger,
    },
  }[variant];

  return (
    <button
      onClick={disabled || loading ? undefined : onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: p,
        borderRadius: 8,
        border: "none",
        fontSize: fs,
        fontWeight: 700,
        fontFamily: "'Syne',sans-serif",
        letterSpacing: "0.03em",
        transition: "all 0.18s",
        opacity: disabled || loading ? 0.45 : 1,
        cursor: disabled || loading ? "not-allowed" : "pointer",
        ...v,
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!disabled && !loading) {
          if (variant === "primary") {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow =
              "0 8px 32px rgba(245,197,24,0.38)";
          }
          if (variant === "secondary")
            e.currentTarget.style.borderColor = C.borderHover;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = v.boxShadow || "";
        if (variant === "secondary")
          e.currentTarget.style.borderColor = C.border;
      }}
    >
      {loading && <Spinner size={13} />}
      {children}
    </button>
  );
};

export const Card = ({ children, style }) => (
  <div
    style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 16,
      padding: 28,
      backdropFilter: "blur(14px)",
      ...style,
    }}
  >
    {children}
  </div>
);

export const Field = ({ label, hint, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
      }}
    >
      <label
        style={{
          fontSize: 10.5,
          fontFamily: "'DM Mono',monospace",
          color: "rgba(245,197,24,0.72)",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </label>
      {hint && <span style={{ fontSize: 10.5, color: C.muted }}>{hint}</span>}
    </div>
    {children}
  </div>
);

const inputStyle = (focused) => ({
  background: focused ? "rgba(245,197,24,0.04)" : C.surface,
  border: `1px solid ${focused ? "rgba(245,197,24,0.38)" : C.border}`,
  borderRadius: 8,
  padding: "11px 14px",
  color: C.text,
  fontSize: 13.5,
  outline: "none",
  transition: "all 0.18s",
  width: "100%",
});

export const FInput = ({ value, onChange, placeholder, type = "text" }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={inputStyle(focused)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

export const FTA = ({ value, onChange, placeholder, rows = 4 }) => {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{ ...inputStyle(focused), resize: "vertical", lineHeight: 1.65 }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};
